import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import type { ReactNode } from 'react';

import { hasValue } from 'utils/hasValue';

import type { IntegrationBusinessUnit } from 'api';

type BusinessUnitsListSync = {
  onBusinessUnitRenamed: (unit: IntegrationBusinessUnit) => void;
  onBusinessUnitSetDefault: (unit: IntegrationBusinessUnit) => void;
  onBusinessUnitDeleted: (unit: IntegrationBusinessUnit) => void;
  onBusinessUnitCreated: () => void;
};

const noop = () => undefined;

const BusinessUnitsListSyncContext = createContext<BusinessUnitsListSync>({
  onBusinessUnitRenamed: noop,
  onBusinessUnitSetDefault: noop,
  onBusinessUnitDeleted: noop,
  onBusinessUnitCreated: noop
});

export const useBusinessUnitsListSync = () =>
  useContext(BusinessUnitsListSyncContext);

export const useBusinessUnitsListRefresh = () => {
  const [listVersion, setListVersion] = useState(0);
  const refreshList = useCallback(
    () => setListVersion((version) => version + 1),
    []
  );
  return [listVersion, refreshList] as const;
};

const matchesSearch = (name: string, term: string) =>
  name.toLowerCase().includes(term.toLowerCase());

type UseBusinessUnitsListSyncValueArgs = {
  businessUnits: IntegrationBusinessUnit[];
  search: string;
  updateElement: (unit: IntegrationBusinessUnit) => void;
  removeElement: (unit: IntegrationBusinessUnit) => void;
  refreshList: () => void;
};

export const useBusinessUnitsListSyncValue = ({
  businessUnits,
  search,
  updateElement,
  removeElement,
  refreshList
}: UseBusinessUnitsListSyncValueArgs): BusinessUnitsListSync =>
  useMemo(
    () => ({
      onBusinessUnitRenamed: (unit) => {
        if (matchesSearch(unit.name, search)) {
          updateElement(unit);
        } else {
          removeElement(unit);
        }
      },
      onBusinessUnitSetDefault: (unit) => {
        updateElement(unit);
        const previousDefault = businessUnits.find(
          (candidate) => candidate.default && candidate.uuid !== unit.uuid
        );
        if (hasValue(previousDefault)) {
          updateElement({ ...previousDefault, default: false });
        }
      },
      onBusinessUnitDeleted: removeElement,
      onBusinessUnitCreated: refreshList
    }),
    [businessUnits, search, updateElement, removeElement, refreshList]
  );

type BusinessUnitsListSyncProviderProps = {
  value: BusinessUnitsListSync;
  children: ReactNode;
};

export const BusinessUnitsListSyncProvider = ({
  value,
  children
}: BusinessUnitsListSyncProviderProps) => (
  <BusinessUnitsListSyncContext.Provider value={value}>
    {children}
  </BusinessUnitsListSyncContext.Provider>
);
